import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authService } from '../../services/api';
import { BarChart3, Layers, FileCheck, Settings, Shield, User, LogOut, X } from 'lucide-react';

const Sidebar = ({ sidebarOpen, setSidebarOpen, currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, path: '/dashboard' },
    { id: 'projects', label: 'Projects', icon: Layers, path: '/projects' },
    { id: 'results', label: 'QA Results', icon: FileCheck, path: '/results' },
    { id: 'settings', label: 'Settings', icon: Settings, path: '/settings' },
  ];

  if (currentUser?.role === 'admin') {
    navItems.push({ id: 'admin', label: 'Admin Panel', icon: Shield, path: '/admin' });
  }
  
  const getCurrentView = () => {
    const path = location.pathname;
    if (path.startsWith('/projects/')) return 'projects';
    if (path === '/projects') return 'projects';
    if (path === '/results') return 'results';
    if (path === '/settings') return 'settings';
    if (path === '/admin') return 'admin';
    return 'dashboard';
  };
  
  const currentView = getCurrentView();
  
  return (
    <>
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      <aside className={`
        fixed left-0 top-0 bottom-0 w-64 bg-[#0E1627] 
        border-r border-[#1F2A44]/80 z-40 flex flex-col
        transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Gradient mesh background */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-20 -left-20 w-60 h-60 bg-[radial-gradient(circle,rgba(79,125,243,0.06)_0%,transparent_70%)]" />
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-[radial-gradient(circle,rgba(79,125,243,0.04)_0%,transparent_70%)]" />
        </div>

        <div className="relative p-6 border-b border-[#1F2A44]/60">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute -inset-1 bg-[radial-gradient(circle,rgba(79,125,243,0.15)_0%,transparent_70%)] rounded-xl" />
                <img 
                  src="/elevatedaudience.png" 
                  alt="Elevated Audience Logo" 
                  className="relative w-10 h-10 object-contain"
                />
              </div>
              <div>
                <h1 className="font-semibold text-[#E6ECF5] tracking-tight">QA Automator</h1>
                <p className="text-[11px] text-[#6B7280] tracking-wide">Elevated Audience</p>
              </div>
            </div>
            <button 
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
              className="lg:hidden p-2 text-[#6B7280] hover:text-[#E6ECF5] hover:bg-[#1F2A44] rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <nav className="relative flex-1 p-4 space-y-0.5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            return (
              <motion.button
                key={item.id}
                whileHover={{ x: 2 }}
                onClick={() => {
                  navigate(item.path);
                  if (window.innerWidth < 1024) {
                    setSidebarOpen(false);
                  }
                }}
                aria-label={`Navigate to ${item.label}`}
                className={`
                  w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium
                  transition-all duration-200 relative
                  ${isActive 
                    ? 'bg-[rgba(79,125,243,0.1)] text-[#E6ECF5] shadow-[inset_0_1px_0_rgba(255,255,255,0.03),0_0_16px_-4px_rgba(79,125,243,0.15)]' 
                    : 'text-[#6B7280] hover:text-[#E6ECF5] hover:bg-[#1F2A44]/40'
                  }
                `}
              >
                {isActive && (
                  <div className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-gradient-to-b from-[#4F7DF3] to-[#4F7DF3]/40" />
                )}
                <Icon className={`w-5 h-5 ${isActive ? 'text-[#4F7DF3]' : ''}`} />
                {item.label}
              </motion.button>
            );
          })}
        </nav>
        
        <div className="relative p-4 border-t border-[#1F2A44]/60">
          <div className="flex items-center gap-3 px-3 py-2.5">
            <div className="w-9 h-9 bg-gradient-to-br from-[#5A88F5] to-[#3E6AE1] rounded-full flex items-center justify-center ring-2 ring-[#4F7DF3]/20 ring-offset-2 ring-offset-[#0E1627]">
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#E6ECF5] truncate">{currentUser?.name || 'User'}</p>
              <p className="text-[11px] text-[#6B7280] truncate capitalize">{currentUser?.role || 'user'}</p>
            </div>
            <button 
              onClick={() => authService.logout().then(() => window.location.reload())}
              aria-label="Logout"
              className="p-2 text-[#6B7280] hover:text-[#E5533D] hover:bg-[rgba(229,83,61,0.08)] rounded-lg transition-all"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
